import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { useMediaQuery } from '@mui/material';
import {
  Button,
  CheckIcon,
  IconButton,
  MicrophoneIcon,
  MicrophoneSlashIcon,
  XIcon,
  chatInputBoxRecipe,
  chatInputControlRecipe,
  chatInputRootRecipe,
  cn,
  hitTarget44,
} from '@natter-co/design-system';
import { useSpeechDictation } from 'modules/forms/hooks';
import { nextInputSource } from 'modules/forms/utils';

import { Waveform } from '../Waveform';
import { FadeIn } from './OpenTextQuestion.styled';

import type { Props } from './types';
import type { DictationStatus } from 'modules/forms/hooks';

const TYPING_IDLE_MS = 1500;
// The textarea grows with its text up to this height, then scrolls.
const MAX_HEIGHT = 200;
// Tailwind's `max-sm` (below 40rem), where the voice buttons drop their labels.
const PHONE_QUERY = '(max-width: 39.98rem)';

// The prototype's lighter resting stroke and a white fill, so the input reads on the grey question box.
// DS gap: ChatInput needs a quieter resting stroke option (its slot pins --foreground-secondary for 3:1 contrast).
const INPUT_SLOTS = {
  '--ds-chat-input-border': 'var(--border-control)',
  '--ds-chat-input-border-hover': 'var(--border-control-hover)',
  '--ds-chat-input-bg': 'var(--background-primary)',
} as CSSProperties;

/**
 * The answer box from the post-event-feedback design: one input built on the DS ChatInput recipes, with everything
 * the person does inside it.
 * - Empty: a one-line pill with a filled primary "Start speaking" on the right (icon-only on a phone).
 * - Waiting for the mic (`starting`): "Waiting for your mic…", the voice button held while the browser asks, and
 *   the X to call it off (the browser's prompt can stay open).
 * - Recording (`listening`): a waveform the full width of the pill, a secondary X (discard, restores the earlier
 *   text) and a primary check (finish, keeps the words).
 * - Filled: the multi-line shape, text on top, then the "transcribed" hint on the left (only after dictation) and a
 *   secondary "Add more" on the right.
 * - Mic blocked: the voice button becomes a secondary mic-off "Try again" (or "Add more" once there is text) and the
 *   placeholder points to typing.
 * - Nothing heard: announced here; the dialog shows the message (`onUnheardChange`) until the person types words or
 *   speaks again.
 * Enter adds a new line; Cmd or Ctrl + Enter calls `onSubmit`. Submitting is otherwise the dialog footer's job.
 *
 * DS gap: ChatInput needs a custom end action slot (a labelled Speak button, a live waveform with discard and
 * finish) and no send button, so this composes the ChatInput recipes around its own textarea.
 */
export const OpenTextQuestion = ({
  question,
  answer,
  onChange,
  onRecordingChange,
  onDictationStatusChange,
  onTypingChange,
  onSubmit,
  onUnheardChange,
  unheardId,
  describedBy,
  autoFocus,
  submitting = false,
}: Props) => {
  const { t, i18n } = useTranslation('forms', { keyPrefix: 'openText' });
  const value = answer?.value ?? '';
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const finishRef = useRef<HTMLButtonElement>(null);
  // The text before the current recording: dictated words are appended to it, and discard restores it.
  const baseRef = useRef<{ value: string; answer: Props['answer']; wasDictated: boolean } | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [wasDictated, setWasDictated] = useState(false);
  const [focused, setFocused] = useState(false);
  const [unheardDismissed, setUnheardDismissed] = useState(false);
  const phone = useMediaQuery(PHONE_QUERY, { noSsr: true });
  const hintId = useId();

  const dictation = useSpeechDictation({
    lang: i18n.language || navigator.language,
    onTranscript: (spoken) => {
      const base = baseRef.current;
      if (!base) return;
      const joined = [base.value, spoken].filter(Boolean).join(' ');
      onChange({
        value: joined.slice(0, question.maxLength),
        inputSource: nextInputSource(base.answer?.inputSource, 'dictated', base.value !== ''),
      });
      setWasDictated(true);
    },
  });

  const { status } = dictation;
  const waiting = status === 'starting';
  const listening = status === 'listening';
  const recording = waiting || listening;
  const blocked = status === 'blocked';
  const showMic = question.dictation && status !== 'unsupported';
  const hasText = value.trim().length > 0;
  // The multi-line shape once there is text; the recording controls always sit in the pill.
  const multiline = hasText && !listening;
  const showHint = multiline && wasDictated;
  const showUnheard = status === 'unheard' && !unheardDismissed;

  // Typing drives the glow. Reported only on a change, and cleared after a pause, a recording or unmount.
  const typingRef = useRef(false);
  const typingTimer = useRef<number>();
  const onTypingChangeRef = useRef(onTypingChange);
  onTypingChangeRef.current = onTypingChange;
  const setTyping = useCallback((typing: boolean) => {
    window.clearTimeout(typingTimer.current);
    if (typing) typingTimer.current = window.setTimeout(() => setTyping(false), TYPING_IDLE_MS);
    if (typingRef.current === typing) return;
    typingRef.current = typing;
    onTypingChangeRef.current?.(typing);
  }, []);
  useEffect(() => () => setTyping(false), [setTyping]);

  // The dialog places the "nothing heard" message (under the question); this decides when it shows.
  const onUnheardChangeRef = useRef(onUnheardChange);
  onUnheardChangeRef.current = onUnheardChange;
  useEffect(() => {
    onUnheardChangeRef.current?.(showUnheard);
  }, [showUnheard]);

  useEffect(() => {
    onRecordingChange(recording);
  }, [recording, onRecordingChange]);

  useEffect(() => {
    if (question.dictation) onDictationStatusChange?.(status);
  }, [status, question.dictation, onDictationStatusChange]);

  // A cleared answer is no longer "your words".
  useEffect(() => {
    if (!hasText && !recording) setWasDictated(false);
  }, [hasText, recording]);

  const focusTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  // The dialog remounts this per step, so this runs when the person arrives at the question.
  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
  }, [autoFocus]);

  // Focus follows the recording: to the check while listening (the textarea is hidden), back to the text after.
  const prevStatus = useRef<DictationStatus | null>(null);
  useEffect(() => {
    const prev = prevStatus.current;
    prevStatus.current = status;
    if (prev === status) return;
    if (status === 'blocked') {
      setAnnouncement(t('live.blocked'));
      focusTextarea();
    } else if (status === 'unheard') {
      setAnnouncement(t('unheard'));
      focusTextarea();
    } else if (status === 'listening') {
      finishRef.current?.focus();
    } else if (status === 'idle' && (prev === 'listening' || prev === 'starting')) {
      focusTextarea();
    }
  }, [status, t, focusTextarea]);

  // Grows with the text. An empty field sizes to its placeholder, so a long one (mic blocked) wraps, not clips.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el || listening) return;
    el.style.height = 'auto';
    let height = el.scrollHeight;
    if (!el.value && el.placeholder) {
      el.value = el.placeholder;
      height = el.scrollHeight;
      el.value = '';
    }
    el.style.height = `${Math.min(height, MAX_HEIGHT)}px`;
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden';
  }, [value, listening, multiline, status]);

  const startRecording = () => {
    baseRef.current = { value: value.trim(), answer, wasDictated };
    setTyping(false);
    setUnheardDismissed(false);
    setAnnouncement(t('live.start'));
    dictation.start();
  };

  const finishRecording = () => {
    setAnnouncement(t('live.finished'));
    dictation.finish();
  };

  const discardRecording = () => {
    const base = baseRef.current;
    baseRef.current = null;
    setAnnouncement(t('live.discarded'));
    dictation.discard();
    if (base) {
      setWasDictated(base.wasDictated);
      onChange(base.answer ?? { value: base.value, inputSource: 'typed' });
    }
  };

  const placeholder = listening
    ? t('listening')
    : waiting
      ? t('waiting')
      : blocked
        ? t('blockedPlaceholder')
        : (question.placeholder ?? t('placeholder'));

  const hit = hitTarget44.from32;
  const voiceLabel = blocked ? t('retry') : t('speak');
  const VoiceIcon = blocked ? MicrophoneSlashIcon : MicrophoneIcon;

  // The browser's mic prompt can stay open indefinitely, so the wait can always be called off.
  const waitingDiscard = waiting && (
    <IconButton
      appearance="secondary"
      size="xs"
      aria-label={t('discard')}
      className={hit}
      disabled={submitting}
      onClick={discardRecording}
    >
      <XIcon />
    </IconButton>
  );

  const actions = (() => {
    if (listening) {
      return (
        <FadeIn className="flex w-full min-w-0 items-center gap-2">
          <Waveform active level={dictation.level} className="mr-2" />
          <IconButton
            appearance="secondary"
            size="xs"
            aria-label={t('discard')}
            className={hit}
            onClick={discardRecording}
          >
            <XIcon />
          </IconButton>
          <IconButton
            ref={finishRef}
            appearance="primary"
            size="xs"
            aria-label={t('finish')}
            className={hit}
            onClick={finishRecording}
          >
            <CheckIcon />
          </IconButton>
        </FadeIn>
      );
    }
    if (multiline) {
      if (!showMic && !showHint) return null;
      return (
        <FadeIn className="flex w-full flex-wrap items-center gap-x-3 gap-y-2">
          {showHint && (
            <span
              id={hintId}
              className="flex min-w-[12rem] flex-1 items-center text-body-sm text-[var(--foreground-secondary)] max-sm:basis-full!"
            >
              {t('transcribed')}
            </span>
          )}
          {showMic && (
            <span className="ms-auto flex items-center gap-2">
              {waitingDiscard}
              <Button
                appearance="secondary"
                size="xs"
                className={hit}
                disabled={waiting || submitting}
                startIcon=<VoiceIcon />
                onClick={startRecording}
              >
                {t('addMore')}
              </Button>
            </span>
          )}
        </FadeIn>
      );
    }
    if (!showMic) return null;
    // Start speaking is the filled primary call to action; Try again is a quieter secondary.
    const tone = blocked ? ({ appearance: 'secondary' } as const) : ({ appearance: 'primary' } as const);
    return (
      <FadeIn className="flex shrink-0 items-center gap-2">
        {waitingDiscard}
        {phone ? (
          <IconButton
            {...tone}
            size="xs"
            aria-label={voiceLabel}
            className={hit}
            disabled={waiting || submitting}
            onClick={startRecording}
          >
            <VoiceIcon />
          </IconButton>
        ) : (
          <Button
            {...tone}
            size="xs"
            className={hit}
            disabled={waiting || submitting}
            startIcon=<VoiceIcon />
            onClick={startRecording}
          >
            {voiceLabel}
          </Button>
        )}
      </FadeIn>
    );
  })();

  return (
    <div className="flex flex-col gap-3">
      <div className={chatInputRootRecipe()} data-focused={focused || listening || undefined} style={INPUT_SLOTS}>
        <div
          className={cn(chatInputBoxRecipe({ multiline }), multiline ? 'gap-2' : 'items-center gap-3')}
          onMouseDown={(event) => {
            // A press on the box's own padding puts the caret in the text, as the DS ChatInput does.
            if (event.target !== event.currentTarget || listening) return;
            event.preventDefault();
            textareaRef.current?.focus();
          }}
        >
          {/* sentry.ts masks replay text only in prod EU, so the answer is blocked here for every environment.
              DS gap: see the component note; the ChatInput recipes style this textarea. */}
          {/* oxlint-disable-next-line react/forbid-elements */}
          <textarea
            ref={textareaRef}
            data-sentry-block
            rows={1}
            value={value}
            maxLength={question.maxLength}
            readOnly={recording || submitting}
            aria-label={t('label')}
            aria-describedby={
              [describedBy, showUnheard && unheardId, showHint && hintId].filter(Boolean).join(' ') || undefined
            }
            aria-busy={listening || undefined}
            placeholder={placeholder}
            className={cn(
              chatInputControlRecipe({ multiline }),
              // MAX_HEIGHT as a class, replacing the recipe's 80px cap; keep the two in step.
              'max-h-[200px]',
              // Multi-line text starts flush with the box's 10px padding, as the DS ChatInput does. The design's
              // `pt-1.5` here loses to the recipe's `p-0` in the playground, so it draws 0px; this matches that.
              multiline ? 'pr-2.5' : 'py-1',
              listening && 'hidden! caret-transparent'
            )}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onChange={(event) => {
              onChange({
                value: event.target.value,
                inputSource: nextInputSource(answer?.inputSource, 'typed', value !== ''),
              });
              setTyping(true);
              // As in the design, only words clear the message; deleting back to blank leaves it.
              if (event.target.value.trim()) setUnheardDismissed(true);
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || !(event.metaKey || event.ctrlKey) || event.nativeEvent.isComposing) return;
              event.preventDefault();
              if (hasText && !recording && !submitting) onSubmit?.();
            }}
          />
          {actions}
        </div>
      </div>

      <div aria-live="polite" className="sr-only">
        {announcement}
      </div>
    </div>
  );
};
